"use client";

import React, { useActionState, useEffect, useRef, useState, startTransition } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import CustomInput from '@/app/_components/CustomInput';
import Heading from '@/app/_components/Heading';
import { Button } from '@/components/ui/button';
import ErrorBanner from '@/app/_components/ErrorBanner';
import { showToast } from '@/app/_components/toast';
import { forgotPasswordSchema } from "@/schema/auth";
import { forgotPasswordAction, type ForgotPasswordState } from "@/actions/auth";
import type { z } from "zod";

interface ForgotPasswordProps {
  onNext?: (phone: string) => void;
}

const ForgotPassword = ({ onNext }: ForgotPasswordProps) => {
  const [state, formAction, isPending] = useActionState<ForgotPasswordState, FormData>(forgotPasswordAction, {
    success: false,
  });

  const [isErrorDismissed, setIsErrorDismissed] = useState(false);

  // react-hook-form for client validation
  type FormValues = z.infer<typeof forgotPasswordSchema>;
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const formRef = useRef<HTMLFormElement | null>(null);
  const phoneValue = ((): string => {
    const el = formRef.current?.elements.namedItem("phone") as HTMLInputElement | undefined;
    return el?.value ?? "";
  })();

  const dismissError = () => {
    setIsErrorDismissed(true);
  };

  // Show toast and advance to next step on successful response
  useEffect(() => {
    if (state.success) {
      if (onNext) {
        onNext(phoneValue);
      }
    } else if (state.message && !state.success) {
      showToast.error(state.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.success, state.message]);

  return (
    <div className="flex max-w-120 flex-col">
      {state.message && !state.success && !isErrorDismissed && (
        <ErrorBanner message={state.message} onDismiss={dismissError} />
      )}
      
      <Heading size="3xl" className="text-text-900 mb-3.5">
        Password Recovery
      </Heading>
      <p className='text-body-medium/md text-text-700 mb-10.5'>Let us help you recover your password.</p>

      <form
        ref={formRef}
        className="mb-4 flex flex-col gap-4"
        noValidate
        onSubmit={handleSubmit(() => {
          setIsErrorDismissed(false);
          const fd = new FormData(formRef.current as HTMLFormElement);
          startTransition(() => {
            formAction(fd);
          });
        })}
      >
        <CustomInput
          label="Enter Phone number"
          type="tel"
          error={errors.phone?.message ?? state.errors?.phone?.[0]}
          {...register("phone")}
          name="phone"
        />

        <Button type="submit" disabled={isPending || isSubmitting}>
          {"Get OTP"}
        </Button>
      </form>
      
    </div>
  )
}

export default ForgotPassword